import { isOk } from "@/common/common";
import {
  AgendaItemType,
  mapToAgendaItem,
  meetingUrl,
  NoticeActiveView,
  NoticeForPowerOfAttorneyRecipientInitializerController,
  toNoticeActiveView,
} from "./generalMeetingDomain";
import { FC, ReactElement, useCallback, useEffect, useMemo } from "react";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { observer } from "mobx-react";
import {
  ExpiredPowerOfAttorneyView,
  PowerOfAttorneyRecipientView,
} from "./generalMeetingComponents";
import {
  onMessage,
  subscribeToGeneralMeetingParticipantMessages,
} from "./participantMessageHandling";
import { useMessageStore, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import LoginPage from "@/Login/LoginPage";
import { MeetingLogin } from "@/meetings/meetingsComponents";
import { noticePoaRecipientUrl } from "@/utils/navigation";

const PowerOfAttorneyRecipientPage: FC<{
  powerOfAttorneyRecipientId: string;
  tab?: string;
  matter?: string;
}> = observer(({ powerOfAttorneyRecipientId, tab, matter }) => {
  const { t } = useTranslation();
  const { http, dispatch } = useRootStore();
  const messageStore = useMessageStore();
  const history = useRootStore().history;
  const ctrl = useMemo(
    () =>
      new NoticeForPowerOfAttorneyRecipientInitializerController(http, messageStore, dispatch, t),
    [dispatch, http, messageStore, t],
  );
  const id = powerOfAttorneyRecipientId;

  useEffect(() => {
    if (isOk(ctrl.result) && ctrl.result.data.response.supersededByPoaRecipientId) {
      history.replace(noticePoaRecipientUrl(ctrl.result.data.response.supersededByPoaRecipientId));
    }
  }, [ctrl.result, history]);

  useEffect(() => {
    if (id) {
      ctrl.fetchPowerOfAttorneyRecipient(id);
    }
  }, [ctrl, id]);

  const openAgendaItem = useCallback(
    (item?: AgendaItemType) => {
      if (isOk(ctrl.result)) {
        history.push(meetingUrl(ctrl.result.data.response, NoticeActiveView.meeting, { item }));
      }
    },
    [ctrl.result, history],
  );

  useEffect(() => {
    if (isOk(ctrl.result) && ctrl.result.data.isLiveGeneralMeeting) {
      const response = ctrl.result.data.response;
      const updateSelectedAttendances = ctrl.result.data.updateSelectedAttendances;
      const handleMessage = onMessage({
        response,
        messageStore,
        openAgendaItem,
        dispatch: dispatch,
        onLinkExpired: ctrl.setLinkExpired,
        updateSelectedAttendances,
      });
      return subscribeToGeneralMeetingParticipantMessages({
        credential: { "poa-recipient-id": response.powerOfAttorneyRecipientId },
        noticeForShareholderIds: [
          ...response.receivedPowerOfAttorneys.map((poa) => poa.noticeForShareholderId),
        ],
        projectId: ctrl.result.data.response.projectId,
        dispatch: dispatch,
        onMessage: handleMessage,
      });
    }
  }, [ctrl.result, messageStore, t, openAgendaItem, dispatch, ctrl.setLinkExpired]);

  const agendaItem = isOk(ctrl.result)
    ? mapToAgendaItem(ctrl.result.data.response, matter)
    : undefined;

  if (ctrl.linkExpired) {
    const companyNameAndOrgNo = isOk(ctrl.result)
      ? [ctrl.result.data.response.companyName, ctrl.result.data.response.orgNo]
      : ["", ""];
    return (
      <ExpiredPowerOfAttorneyView
        companyName={companyNameAndOrgNo[0]}
        orgNo={companyNameAndOrgNo[1]}
      />
    );
  } else {
    return renderPoAView(ctrl, id, openAgendaItem, agendaItem, tab);
  }
});

function renderPoAView(
  ctrl: NoticeForPowerOfAttorneyRecipientInitializerController,
  powerOfAttorneyRecipientId: string,
  openAgendaItem: (agendaItem?: AgendaItemType) => void,
  agendaItem?: AgendaItemType,
  activeView?: string,
): ReactElement {
  const result = ctrl.result;
  switch (result.state) {
    case "pending":
      return <LoadingIndicator fullscreen />;
    case "failure":
      if (result.error.authStatus) {
        if (result.error.authStatus === "LOGIN_NEEDED") {
          return <LoginPage />;
        } else {
          return (
            <MeetingLogin
              authStatus={result.error.authStatus}
              companyName={result.error.companyName}
              companyLogo={result.error.companyLogo}
              meetingPassword={ctrl.meetingPassword}
              onSubmit={() => {
                ctrl.fetchPowerOfAttorneyRecipient(powerOfAttorneyRecipientId);
              }}
            />
          );
        }
      }

      return <ExpiredPowerOfAttorneyView companyName={""} orgNo={""} />;
    case "cancelled":
      return <div />;
    case "ok":
      return (
        <PowerOfAttorneyRecipientView
          controller={result.data}
          activeView={toNoticeActiveView(activeView)}
          agendaItem={agendaItem}
          openAgendaItem={openAgendaItem}
        />
      );
  }
}

export default PowerOfAttorneyRecipientPage;
